import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import config from '../config/config';
import { normalizeUserProfile } from '../utils/userProfile';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(config.TOKEN_STORAGE_KEY);
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await api.get(config.API_ENDPOINTS.AUTH.ME);
      const normalizedUser = normalizeUserProfile(response.data.user || {});
      setUser(normalizedUser);
      localStorage.setItem(config.USER_STORAGE_KEY, JSON.stringify(normalizedUser));
    } catch (error) {
      console.error('Error fetching user:', error);
      localStorage.removeItem(config.TOKEN_STORAGE_KEY);
      localStorage.removeItem(config.USER_STORAGE_KEY);
      delete api.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (emailOrEmployeeId, password) => {
    const trimmed = String(emailOrEmployeeId ?? '').trim();
    try {
      const idOrEmail = String(emailOrEmployeeId || '').trim();
      console.log('🌐 Making login API request...');
      console.log('📍 API URL:', `${config.API_BASE_URL}${config.API_ENDPOINTS.AUTH.LOGIN}`);
      console.log('📦 Request payload:', { emailOrEmployeeId: trimmed, password: '***' });
      
      const response = await api.post(config.API_ENDPOINTS.AUTH.LOGIN, { 
        emailOrEmployeeId: trimmed, 
        password 
      });
      
      console.log('✅ API Response received:', response.status);
      console.log('📄 Response data:', response.data);
      
      const { token, user } = response.data;
      const normalizedUser = normalizeUserProfile(user || {});
      
      // Debug: Log the user data received from backend
      console.log('Login response user data:', normalizedUser);
      console.log('User role:', normalizedUser.role);
      console.log('User employeeId:', normalizedUser.employeeId);
      console.log('User email:', normalizedUser.email);
      
      localStorage.setItem(config.TOKEN_STORAGE_KEY, token);
      localStorage.setItem(config.USER_STORAGE_KEY, JSON.stringify(normalizedUser));
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(normalizedUser);
      
      toast.success('Login successful!');
      return { success: true, user: normalizedUser };
    } catch (error) {
      console.error('❌ Login API Error:', error);
      console.error('📊 Error response:', error.response?.data);
      console.error('🔢 Status code:', error.response?.status);
      console.error('🌐 Request URL:', error.config?.url);
      console.error('📦 Request data:', error.config?.data);
      
      let message = error.response?.data?.message || 'Login failed';
      if (error.response?.status === 401) {
        const m = String(message).toLowerCase();
        if (m.includes('deactivated')) {
          message =
            'Your account is pending approval or has been deactivated. Please contact your administrator.';
        }
      }
      toast.error(message);
      return { success: false, message };
    }
  };

  const logout = async () => {
    try {
      await api.post(config.API_ENDPOINTS.AUTH.LOGOUT);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem(config.TOKEN_STORAGE_KEY);
      localStorage.removeItem(config.USER_STORAGE_KEY);
      delete api.defaults.headers.common['Authorization'];
      setUser(null);
      toast.success('Logged out successfully');
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post(config.API_ENDPOINTS.AUTH.REGISTER, userData);
      const { token, user } = response.data;
      const normalizedUser = normalizeUserProfile(user || {});
      
      localStorage.setItem(config.TOKEN_STORAGE_KEY, token);
      localStorage.setItem(config.USER_STORAGE_KEY, JSON.stringify(normalizedUser));
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(normalizedUser);
      
      toast.success('Registration successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const updateUser = (updatedUserData) => {
    setUser(prevUser => {
      const mergedUser = normalizeUserProfile({
        ...(prevUser || {}),
        ...(updatedUserData || {})
      });

      localStorage.setItem(config.USER_STORAGE_KEY, JSON.stringify(mergedUser));
      return mergedUser;
    });
  };

  const value = {
    user,
    loading,
    login,
    logout,
    register,
    fetchUser,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
